import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
  getDoc,
} from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const YieldTestingTool = () => {
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [daysToSet, setDaysToSet] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState("");
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [simulationMode, setSimulationMode] = useState("days"); // "days", "period", or "daterange"
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserEmail, setSelectedUserEmail] = useState("");

  useEffect(() => {
    console.log("YieldTestingTool: Component mounted, loading data...");
    loadAllUsers();
    loadProducts();
  }, []);

  useEffect(() => {
    if (selectedUserEmail) {
      loadUserData(selectedUserEmail);
    }
  }, [selectedUserEmail]);

  // Reload products when user data changes (to filter by access)
  useEffect(() => {
    if (userData) {
      loadProducts();
    }
  }, [userData]);

  const loadAllUsers = async () => {
    try {
      console.log("YieldTestingTool: Loading all users...");
      const usersSnapshot = await getDocs(collection(DATABASE, "users"));
      const users = usersSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          email: doc.data().email,
          name: doc.data().full_name || doc.data().name || doc.data().email,
        }))
        .filter((user) => user.email)
        .sort((a, b) => a.name.localeCompare(b.name));

      console.log(`YieldTestingTool: Loaded ${users.length} users`);
      setAllUsers(users);
      
      // Default to jrmascarin if available
      const defaultUser = users.find((u) => u.email === "jrmascarin@gmail.com");
      if (defaultUser) {
        setSelectedUserEmail(defaultUser.email);
      }
    } catch (error) {
      console.error("YieldTestingTool: Error loading users:", error);
      toast.error("Failed to load users");
    }
  };

  const loadUserData = async (email) => {
    setLoading(true);
    try {
      console.log("YieldTestingTool: Loading user data for", email);
      // Find user by email
      const usersQuery = query(
        collection(DATABASE, "users"),
        where("email", "==", email)
      );
      const usersSnapshot = await getDocs(usersQuery);

      if (usersSnapshot.empty) {
        console.warn("YieldTestingTool: User not found");
        toast.error(`User with email ${email} not found`);
        setUserData(null);
        setLoading(false);
        return;
      }

      const userDoc = usersSnapshot.docs[0];
      const user = {
        id: userDoc.id,
        ...userDoc.data(),
      };

      console.log("YieldTestingTool: User loaded", user.id);
      setUserData(user);
    } catch (error) {
      console.error("YieldTestingTool: Error loading user data:", error);
      toast.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      // Load all products
      const snapshot = await getDocs(collection(DATABASE, "yields_products"));
      
      // Load finalized products to filter them out
      const finalSnapshot = await getDocs(collection(DATABASE, "yields_final"));
      const finalizedProductIds = new Set();
      finalSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.product_id) {
          finalizedProductIds.add(data.product_id);
        }
      });

      // Filter products based on user access and status
      const allProducts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter products: only show products that:
      // 1. User has access to (public OR restricted with user_id in allowed_user_ids)
      // 2. Are not finalized (no record in yields_final)
      // 3. Are active (active = true)
      const filteredProducts = allProducts.filter((product) => {
        // Check if product is active
        if (!product.active) return false;

        // Check if product is finalized
        if (finalizedProductIds.has(product.product_id)) return false;

        // If no user selected, show all non-finalized active products
        if (!userData) return true;

        // Check access: public OR restricted with user access
        const accessType = product.access_type || "public";
        if (accessType === "public") {
          return true; // Public products - all users have access
        }

        // Restricted products - check if user is in allowed_user_ids
        if (accessType === "restricted") {
          const allowedUserIds = product.allowed_user_ids || [];
          return allowedUserIds.includes(userData.id);
        }

        return false;
      });

      // Sort by creation date (newest first)
      filteredProducts.sort((a, b) => {
        const aTime = a.created_at?.toMillis() || 0;
        const bTime = b.created_at?.toMillis() || 0;
        return bTime - aTime;
      });

      setProducts(filteredProducts);
    } catch (error) {
      console.error("Error loading products:", error);
      toast.error("Failed to load products");
    }
  };

  const handleSetDays = async (productId, days) => {
    if (!userData) {
      toast.error("User data not loaded");
      return;
    }

    try {
      setLoading(true);
      const userRef = doc(DATABASE, "users", userData.id);
      const currentDays = userData.yield_days_participated || {};
      const updatedDays = {
        ...currentDays,
        [productId]: days,
      };

      await updateDoc(userRef, {
        yield_days_participated: updatedDays,
      });

      const product = products.find(p => p.product_id === productId);
      toast.success(`Successfully set ${days} days for ${product?.product_name || productId}`);
      await loadUserData(selectedUserEmail); // Reload to show updated data
    } catch (error) {
      console.error("Error setting days:", error);
      toast.error("Failed to set participation days");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSet = (productId, days) => {
    const product = products.find(p => p.product_id === productId);
    const currentDays = getCurrentDays(productId);
    
    setPreviewData({
      productId,
      productName: product?.product_name || productId,
      currentDays,
      newDays: days,
    });
    setPreviewDialogOpen(true);
  };

  const handleOpenEditDialog = (productId) => {
    const currentDays = getCurrentDays(productId);
    setEditingProductId(productId);
    setDaysToSet(currentDays);
    setSimulationMode("days");
    setSelectedMonth(new Date().getMonth() + 1);
    setSelectedYear(new Date().getFullYear());
    setStartDate(dayjs());
    setEndDate(dayjs());
    setEditDialogOpen(true);
  };

  // Calculate days for a specific month/year
  const calculateDaysForPeriod = (month, year) => {
    // Get number of days in the month
    const daysInMonth = new Date(year, month, 0).getDate();
    return daysInMonth;
  };

  // Calculate days between start and end date (inclusive)
  const calculateDaysForDateRange = (start, end) => {
    if (!start || !end) return 0;
    const startDayjs = dayjs(start);
    const endDayjs = dayjs(end);
    if (endDayjs.isBefore(startDayjs)) return 0;
    // Add 1 to include both start and end dates
    return endDayjs.diff(startDayjs, 'day') + 1;
  };

  // Get month name
  const getMonthName = (month) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[month - 1] || "";
  };

  const handlePreviewConfirm = async () => {
    if (previewData) {
      setPreviewDialogOpen(false);
      await handleSetDays(previewData.productId, previewData.newDays);
      setPreviewData(null);
    }
  };

  const handleEditDialogConfirm = async () => {
    let daysToApply = daysToSet;
    let periodInfo = null;

    // If period mode, calculate days from month/year
    if (simulationMode === "period") {
      daysToApply = calculateDaysForPeriod(selectedMonth, selectedYear);
      periodInfo = `${getMonthName(selectedMonth)} ${selectedYear}`;
    } else if (simulationMode === "daterange") {
      // If date range mode, calculate days from date range
      daysToApply = calculateDaysForDateRange(startDate, endDate);
      if (daysToApply > 0) {
        periodInfo = `${startDate.format("DD/MM/YYYY")} to ${endDate.format("DD/MM/YYYY")}`;
      }
    }

    if (editingProductId && daysToApply >= 0) {
      // Validate date range
      if (simulationMode === "daterange" && daysToApply === 0) {
        toast.error("End date must be on or after start date");
        return;
      }

      const product = products.find(p => p.product_id === editingProductId);
      setEditDialogOpen(false);
      
      // Show preview before applying
      setPreviewData({
        productId: editingProductId,
        productName: product?.product_name || editingProductId,
        currentDays: getCurrentDays(editingProductId),
        newDays: daysToApply,
        periodInfo: periodInfo,
      });
      setPreviewDialogOpen(true);
      
      // Reset edit dialog state
      setEditingProductId("");
      setDaysToSet(0);
      setSimulationMode("days");
    }
  };

  const handleResetToggleCount = async () => {
    if (!userData) return;

    const result = await Swal.fire({
      title: "Reset Toggle Count?",
      text: "This will reset yield_toggles_this_month to 0",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#00ba2d",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Yes, Reset",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const userRef = doc(DATABASE, "users", userData.id);
      await updateDoc(userRef, {
        yield_toggles_this_month: 0,
      });

      toast.success("Toggle count reset successfully");
      await loadUserData(selectedUserEmail);
    } catch (error) {
      console.error("Error resetting toggle count:", error);
      toast.error("Failed to reset toggle count");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentDays = (productId) => {
    if (!userData?.yield_days_participated) return 0;
    return userData.yield_days_participated[productId] || 0;
  };

  console.log("YieldTestingTool: Rendering component, userData:", userData, "loading:", loading);

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Yield Testing Tool
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => loadUserData(selectedUserEmail)}
          disabled={loading || !selectedUserEmail}
        >
          Refresh
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            This tool allows you to simulate participation days for Yield testing without waiting for real days to pass.
          </Typography>
          <TextField
            select
            fullWidth
            label="Select User"
            value={selectedUserEmail}
            onChange={(e) => setSelectedUserEmail(e.target.value)}
            disabled={loading}
            SelectProps={{ native: true }}
          >
            <option value="">-- Select a user --</option>
            {allUsers.map((user) => (
              <option key={user.id} value={user.email}>
                {user.name} ({user.email})
              </option>
            ))}
          </TextField>
        </CardContent>
      </Card>

      {loading && !userData ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : !userData ? (
        <Alert severity="error">
          User not found. Please make sure the user exists in the database.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* User Status Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  User Status
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <strong>Name:</strong>
                        </TableCell>
                        <TableCell>{userData.full_name || userData.name || "N/A"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Email:</strong>
                        </TableCell>
                        <TableCell>{userData.email}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Yield Active:</strong>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={userData.yield_active ? "Yes" : "No"}
                            color={userData.yield_active ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Active Product ID:</strong>
                        </TableCell>
                        <TableCell>
                          {userData.yield_active_product_id || (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              None
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Toggles This Month:</strong>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip
                              label={userData.yield_toggles_this_month || 0}
                              color="primary"
                              size="small"
                            />
                            <Tooltip title="Reset toggle count">
                              <IconButton
                                size="small"
                                onClick={handleResetToggleCount}
                                disabled={loading}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Total Earned:</strong>
                        </TableCell>
                        <TableCell>
                          ${Number(userData.yield_total_earned || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Participation Days Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Participation Days by Product
                </Typography>
                {products.length === 0 ? (
                  <Alert severity="info">No products found. Create a product first.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="center">Current Days</TableCell>
                          <TableCell align="center">Quick Set</TableCell>
                          <TableCell align="center">Custom</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {products.map((product) => {
                          const currentDays = getCurrentDays(product.product_id);
                          return (
                            <TableRow key={product.id}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                  {product.product_name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {product.product_id}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={currentDays}
                                  color={currentDays > 0 ? "success" : "default"}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                  <Tooltip title="Set 7 days">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleQuickSet(product.product_id, 7)}
                                      disabled={loading}
                                      sx={{ minWidth: "40px" }}
                                    >
                                      7
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Set 15 days">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleQuickSet(product.product_id, 15)}
                                      disabled={loading}
                                      sx={{ minWidth: "40px" }}
                                    >
                                      15
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Set 30 days">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      onClick={() => handleQuickSet(product.product_id, 30)}
                                      disabled={loading}
                                      sx={{ minWidth: "40px" }}
                                    >
                                      30
                                    </Button>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Set custom days">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenEditDialog(product.product_id)}
                                    disabled={loading}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Instructions Card */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
                  📖 How to Use This Tool
                </Typography>
                <Typography variant="body1" component="div" sx={{ fontSize: "1.1rem", lineHeight: 1.8, mb: 2 }}>
                  <strong style={{ fontSize: "1.2rem" }}>Step 1: Set Participation Days</strong>
                  <br />
                  Use the quick buttons (7, 15, 30 days) or click the edit icon to set a custom number of days. 
                  A preview dialog will show you the current vs. new days before applying the change.
                  <br /><br />
                  
                  <strong style={{ fontSize: "1.2rem" }}>Step 2: Test Monthly Processing</strong>
                  <br />
                  Go to <strong>Yield Monthly Processor</strong> → Select the product → Select month/year → 
                  Enter a monthly percentage (e.g., 1.5%) → Click "Calculate". 
                  You should see your user with the simulated days of participation and calculated yield amounts (Gross, Commission, Net).
                  <br /><br />
                  
                  <strong style={{ fontSize: "1.2rem" }}>Step 3: Process Monthly Yield</strong>
                  <br />
                  Review the calculations, adjust individual percentages or commission rates if needed, then click "Submit Monthly Yield" to save the monthly record.
                  <br /><br />
                  
                  <strong style={{ fontSize: "1.2rem" }}>Step 4: Test Final Submission</strong>
                  <br />
                  For final submission testing, you need multiple monthly records:
                  <br />
                  • Set 30 days → Process Month 1
                  <br />
                  • Set 60 days → Process Month 2  
                  <br />
                  • Set 90 days → Process Month 3
                  <br />
                  Then go to <strong>Yield Final Submission</strong> → Select product → Click "Calculate Final" to see aggregated totals → Submit final yield.
                  <br /><br />
                  
                  <strong style={{ fontSize: "1.2rem", color: "#ff9800" }}>⚠️ What to Expect:</strong>
                  <br />
                  • After setting days, the "Current Days" chip will update immediately
                  <br />
                  • In Monthly Processor, you'll see calculations based on: (Net Balance × Daily % × Days Participated) / 100
                  <br />
                  • Commission is calculated as: (Gross Yield × Commission %) / 100
                  <br />
                  • Net Yield = Gross Yield - Commission (this is what users receive)
                  <br />
                  • Final submission creates transactions with NET amounts and updates user balances
                  <br /><br />
                  
                  <strong style={{ fontSize: "1.1rem" }}>📝 Important Notes:</strong>
                  <br />
                  • This tool only modifies <code>yield_days_participated</code> in Firestore
                  <br />
                  • Make sure the user has <code>yield_active = true</code> and <code>yield_active_product_id</code> set correctly
                  <br />
                  • Days are stored per product: <code>yield_days_participated[product_id] = days</code>
                  <br />
                  • You can reset toggle count using the refresh icon next to "Toggles This Month"
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => {
        setEditDialogOpen(false);
        setEditingProductId("");
        setDaysToSet(0);
        setSimulationMode("days");
        setStartDate(dayjs());
        setEndDate(dayjs());
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Set Participation Days</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Product"
              value={products.find(p => p.product_id === editingProductId)?.product_name || editingProductId}
              disabled
              sx={{ mb: 3 }}
              helperText={`Product ID: ${editingProductId}`}
            />

            <Divider sx={{ my: 2 }} />

            {/* Simulation Mode Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                Simulation Mode
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant={simulationMode === "days" ? "contained" : "outlined"}
                  onClick={() => setSimulationMode("days")}
                  size="small"
                >
                  Custom Days
                </Button>
                <Button
                  variant={simulationMode === "period" ? "contained" : "outlined"}
                  onClick={() => setSimulationMode("period")}
                  size="small"
                  color="primary"
                >
                  By Period (Month/Year)
                </Button>
                <Button
                  variant={simulationMode === "daterange" ? "contained" : "outlined"}
                  onClick={() => setSimulationMode("daterange")}
                  size="small"
                  color="secondary"
                >
                  By Date Range
                </Button>
              </Box>
            </Box>

            {/* Days Mode */}
            {simulationMode === "days" && (
              <TextField
                fullWidth
                type="number"
                label="Days to Set"
                value={daysToSet}
                onChange={(e) => setDaysToSet(parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, step: 1 }}
                helperText="Enter the number of days to simulate participation"
              />
            )}

            {/* Period Mode */}
            {simulationMode === "period" && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      select
                      label="Month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
                        <option key={month} value={month}>
                          {getMonthName(month)}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Year"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value) || new Date().getFullYear())}
                      inputProps={{ min: 2020, max: 2030, step: 1 }}
                    />
                  </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Calculated Days:</strong> {calculateDaysForPeriod(selectedMonth, selectedYear)} days
                    <br />
                    <small>This is the number of days in {getMonthName(selectedMonth)} {selectedYear}</small>
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Date Range Mode */}
            {simulationMode === "daterange" && (
              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => {
                          setStartDate(newValue);
                          // Auto-adjust end date if it's before start date
                          if (newValue && endDate && dayjs(endDate).isBefore(newValue)) {
                            setEndDate(newValue);
                          }
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => {
                          setEndDate(newValue);
                        }}
                        minDate={startDate}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </LocalizationProvider>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Calculated Days:</strong> {calculateDaysForDateRange(startDate, endDate)} days
                    <br />
                    <small>
                      {startDate && endDate && dayjs(endDate).isSameOrAfter(startDate) ? (
                        <>
                          This will create 1 day of participation for each day from {startDate.format("DD/MM/YYYY")} to {endDate.format("DD/MM/YYYY")} (inclusive)
                        </>
                      ) : (
                        "Please select valid start and end dates"
                      )}
                    </small>
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditingProductId("");
            setDaysToSet(0);
            setSimulationMode("days");
            setStartDate(dayjs());
            setEndDate(dayjs());
          }} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleEditDialogConfirm} 
            variant="contained" 
            color="success" 
            disabled={
              (simulationMode === "days" && daysToSet < 0) ||
              (simulationMode === "daterange" && calculateDaysForDateRange(startDate, endDate) === 0)
            }
          >
            Preview & Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview/Simulation Dialog */}
      <Dialog 
        open={previewDialogOpen} 
        onClose={() => {
          setPreviewDialogOpen(false);
          setPreviewData(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>
          📊 Preview: Participation Days Change
        </DialogTitle>
        <DialogContent>
          {previewData && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: "bold", mb: 1 }}>
                  Review the changes before applying:
                </Typography>
                <Typography variant="body2">
                  This will update the participation days in Firestore. You can see the results in Yield Monthly Processor after applying.
                </Typography>
              </Alert>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                        Product
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {previewData.productName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        ID: {previewData.productId}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                        User
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {userData?.full_name || userData?.name || "N/A"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {selectedUserEmail}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                  Days Comparison
                </Typography>
                {previewData.periodInfo && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Period:</strong> {previewData.periodInfo}
                      <br />
                      <small>Days calculated automatically from the selected month/year</small>
                    </Typography>
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Card sx={{ backgroundColor: "#f5f5f5" }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                          Current Days
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: "bold", color: "#666" }}>
                          {previewData.currentDays}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card sx={{ backgroundColor: "#e8f5e9" }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                          New Days
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: "bold", color: "#00ba2d" }}>
                          {previewData.newDays}
                        </Typography>
                        {previewData.periodInfo && (
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                            ({previewData.periodInfo})
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>What will happen:</strong>
                  <br />
                  • Field <code>yield_days_participated[{previewData.productId}]</code> will be updated to <strong>{previewData.newDays}</strong>
                  <br />
                  • This change will be reflected immediately in the table above
                  <br />
                  • You can then test monthly processing in <strong>Yield Monthly Processor</strong>
                </Typography>
              </Alert>

              {previewData.newDays !== previewData.currentDays && (
                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>Change:</strong> {previewData.newDays > previewData.currentDays ? "+" : ""}
                    {previewData.newDays - previewData.currentDays} days
                    {previewData.newDays > previewData.currentDays ? " (increase)" : " (decrease)"}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setPreviewDialogOpen(false);
              setPreviewData(null);
            }} 
            color="inherit"
            size="large"
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePreviewConfirm} 
            variant="contained" 
            color="success"
            size="large"
            disabled={loading}
            sx={{ minWidth: "150px" }}
          >
            {loading ? <CircularProgress size={24} /> : "Apply Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YieldTestingTool;

